/**
 * <norse-subscription-toggle-buy-box>
 *
 * "Subscribe & save / Buy once" toggle. Only the FIRST selling plan is used —
 * each variant's canonical plan ID (allocation slot 0) is precomputed in Liquid
 * and shipped in an inline JSON map, so no product.js fetch is needed.
 *
 * On toggle / variant change: syncs the selling_plan hidden input, the price
 * row (actual + compare + save badge), the savings message and the ATC button.
 */

if (!customElements.get('norse-subscription-toggle-buy-box')) {

  /**
   * Bundle variants use different selling_plan ids than the 1-pack UI group.
   * Shared resolution (direct id → ~40-day name match → slot-index fallback)
   * lives in norse-selling-plan-utils.js.
   */
  function nstbbResolveCanonicalPlanId(product, variant, uiPlanId) {
    if (!uiPlanId) return '';
    const utils = window.NorseSellingPlanUtils;
    if (utils) return utils.canonicalPlanId(product, variant, uiPlanId);

    // Minimal fallback (utils script missing): direct id match only
    const uiPlanIdN = Number(uiPlanId);
    const direct = (variant.selling_plan_allocations || []).find(
      a => Number(a.selling_plan_id) === uiPlanIdN || Number(a.selling_plan?.id) === uiPlanIdN
    );
    const sid = direct?.selling_plan_id ?? direct?.selling_plan?.id;
    return sid != null ? String(sid) : String(uiPlanId);
  }

  /**
   * Exact cents in the market's punctuation — see NorseSellingPlanUtils.formatMoney.
   * This used to round to whole units and fall back to `Intl` with the en-US locale, which
   * printed a price the cart never charges in any converted market ("€42" for €41,95) and
   * in the wrong punctuation ("SEK 774" beside Liquid's "774 kr").
   */
  function nstbbFormatMoney(cents) {
    const shared = window.NorseSellingPlanUtils?.formatMoney;
    if (shared) return shared(cents);
    return (Math.round(Number(cents) || 0) / 100).toFixed(2).replace(/\.00$/, '');
  }

  customElements.define(
    'norse-subscription-toggle-buy-box',
    class NorseSubscriptionToggleBuyBox extends HTMLElement {

      connectedCallback() {
        this._fetchRunId = 0;
        this._variants = this._parseVariants();
        this._bindToggle();
        this._markKeepBenefit();
        this._bindVariantChanges();
        this._syncAll();
      }

      // ── accessors ────────────────────────────────────────────────────────

      get sectionId() {
        const declared = this.dataset.sectionId || '';
        if (declared && document.getElementById(`product-form-${declared}`)) return declared;
        const infoSection = this.closest('[id^="ProductInfo-"]');
        if (infoSection) {
          const id = infoSection.id.replace('ProductInfo-', '');
          if (id && document.getElementById(`product-form-${id}`)) return id;
        }
        return declared;
      }

      get isSubscriptionSelected() {
        return this.classList.contains('is-sub');
      }

      // ── init helpers ─────────────────────────────────────────────────────

      _parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-nstbb-variants]')?.textContent || '{}');
        } catch {
          return {};
        }
      }

      /** Mark the benefit that keeps its green check in Buy once mode. */
      _markKeepBenefit() {
        const keepText = (this.dataset.keepBenefit || '').trim().toLowerCase();
        if (!keepText) return;
        this.querySelectorAll('[data-nstbb-benefits] li').forEach(li => {
          if (li.textContent.toLowerCase().includes(keepText)) {
            li.classList.add('nstbb__benefit--keep');
          }
        });
      }

      _bindToggle() {
        this.querySelectorAll('[data-nstbb-toggle]').forEach(btn => {
          btn.addEventListener('click', () => this._selectPlan(btn.dataset.nstbbToggle));
        });
      }

      /** Follow variant changes made by other pickers on the page. */
      _bindVariantChanges() {
        this._onVariantChange = e => {
          if (e?.detail?.source === 'norse-subscription-toggle-buy-box') return;
          this._syncAll();
        };
        document.addEventListener('variant:change', this._onVariantChange);

        const form = document.getElementById(`product-form-${this.sectionId}`);
        const idInput = form?.querySelector('input[name="id"]');
        if (idInput) idInput.addEventListener('change', () => this._syncAll());
      }

      disconnectedCallback() {
        if (this._onVariantChange) {
          document.removeEventListener('variant:change', this._onVariantChange);
        }
      }

      // ── state ────────────────────────────────────────────────────────────

      _selectPlan(type) {
        const isSub = type === 'sub';
        this.classList.toggle('is-sub', isSub);
        this.classList.toggle('is-ot', !isSub);
        this.querySelector('[data-nstbb-toggle="sub"]')?.setAttribute('aria-pressed', String(isSub));
        this.querySelector('[data-nstbb-toggle="ot"]')?.setAttribute('aria-pressed', String(!isSub));
        if (!isSub) this._fetchRunId += 1;
        this._syncAll();
      }

      _currentVariantData() {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        const variantId = form?.querySelector('input[name="id"]')?.value;
        if (variantId && this._variants[variantId]) return this._variants[variantId];
        // Fallback: first variant in the map
        const first = Object.keys(this._variants)[0];
        return first ? this._variants[first] : null;
      }

      _getPairedSpl() {
        const root =
          document.getElementById(`ProductInfo-${this.sectionId}`) ||
          this.closest('[id^="ProductInfo-"]') ||
          document;
        return root.querySelector(`selling-plans-alt[data-section-id="${this.sectionId}"]`);
      }

      /** Delivery frequency from paired selling-plans-alt, else forced/first allocation on the variant. */
      _getUiPlanId() {
        if (this.dataset.force60PlanId) {
          return this.dataset.force60PlanId;
        }
        const spl = this._getPairedSpl();
        const deliveryChecked = spl?.querySelector('.selling-plans-alt__delivery-input:checked');
        if (deliveryChecked) {
          return deliveryChecked.dataset.sellingPlanId || deliveryChecked.value || '';
        }
        return this._currentVariantData()?.sp || '';
      }

      /**
       * Fetch product.js, resolve canonical selling_plan_id for current variant +
       * selected delivery slot, and write it to the hidden form input.
       */
      async _resolveAndSyncPlanId() {
        if (!this.isSubscriptionSelected) {
          this._fetchRunId += 1;
          this._writeSellingPlanInput('');
          return;
        }

        const uiPlanId = this._getUiPlanId();
        const form = document.getElementById(`product-form-${this.sectionId}`);
        const variantId = form?.querySelector('input[name="id"]')?.value;
        const handle = this.dataset.productHandle;

        if (!uiPlanId || !variantId || !handle) {
          this._writeSellingPlanInput(uiPlanId || '');
          return;
        }

        this._writeSellingPlanInput(uiPlanId);

        const runId = ++this._fetchRunId;
        try {
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`);
          const product = await res.json();
          if (runId !== this._fetchRunId) return;
          if (!this.isSubscriptionSelected) {
            this._writeSellingPlanInput('');
            return;
          }

          const variant = product.variants.find(v => String(v.id) === String(variantId));
          if (!variant) return;

          this._writeSellingPlanInput(nstbbResolveCanonicalPlanId(product, variant, uiPlanId));
        } catch {
          // Keep the UI plan ID already written above
        }
      }

      // ── sync ─────────────────────────────────────────────────────────────

      _syncAll() {
        const data = this._currentVariantData();
        if (!data) return;
        const isSub = this.isSubscriptionSelected;

        // Selling plan input — resolve canonical id for bundle variants + delivery slot
        if (isSub) {
          void this._resolveAndSyncPlanId();
        } else {
          this._writeSellingPlanInput('');
        }

        // Actual price
        const priceEl = this.querySelector('[data-nstbb-price]');
        if (priceEl) priceEl.textContent = nstbbFormatMoney(isSub ? data.sub : data.ot);

        // Compare price shows in both states (vs the displayed price);
        // the save badge + savings message are subscription-only (CSS hides in ot)
        // Exact cents — the cart charges them, so the header must show them.
        const cmpR  = Math.round(data.cmp);
        const subR  = Math.round(data.sub);
        const otR   = Math.round(data.ot);
        const saveR = cmpR - subR;
        const shownR = isSub ? subR : otR;

        const cmpEl = this.querySelector('[data-nstbb-compare]');
        if (cmpEl) {
          cmpEl.textContent = nstbbFormatMoney(cmpR);
          cmpEl.classList.toggle('is-hidden', cmpR <= shownR);
        }

        const badgeEl = this.querySelector('[data-nstbb-save-badge]');
        if (badgeEl) {
          const pct = cmpR > 0 ? Math.round((saveR / cmpR) * 100) : 0;
          badgeEl.textContent = pct > 0 ? `SAVE ${pct}%` : '';
          badgeEl.classList.toggle('is-hidden', pct <= 0);
        }

        // Savings message amount
        const msgEl = this.querySelector('[data-nstbb-savings-msg]');
        const amountEl = this.querySelector('[data-nstbb-savings-amount]');
        if (amountEl) amountEl.textContent = nstbbFormatMoney(saveR);
        if (msgEl) msgEl.classList.toggle('is-hidden', saveR <= 0);

        this._updateButtonText(isSub ? data.sub : data.ot);
        this._updateGalleryBadge(cmpR > 0 ? Math.round(((cmpR - shownR) / cmpR) * 100) : 0);
        this._dispatchChange(data, isSub);
      }

      /** Keep the "Save X%" badge over the galleries (default media gallery + custom gallery) in sync. */
      _updateGalleryBadge(percent) {
        const pct = Math.max(0, Math.round(Number(percent) || 0));
        const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
        if (gallery && typeof gallery.setSavePercent === 'function') {
          gallery.setSavePercent(pct);
        }
        const wrapper = this.closest('.product')?.querySelector('.product__media-wrapper');
        wrapper?.querySelectorAll('.custom-gallery [data-product-media-discount-badge]').forEach(badge => {
          const el = badge.querySelector('[data-discount-percent]');
          if (el) el.textContent = String(pct);
          badge.classList.toggle('product__media-discount-badge--hidden', pct <= 0);
        });
      }

      _writeSellingPlanInput(planId) {
        const hidden = this.querySelector('[data-nstbb-selling-plan-input]');
        if (!hidden) return;
        hidden.value = planId ? String(planId) : '';
        const realFormId = `product-form-${this.sectionId}`;
        if (hidden.getAttribute('form') !== realFormId) {
          hidden.setAttribute('form', realFormId);
        }
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }

      _updateButtonText(lineCents) {
        if (!(lineCents > 0)) return;
        const btn = document.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        const span = btn?.querySelector('span');
        if (span) span.textContent = `ADD TO CART - ${nstbbFormatMoney(lineCents)}`;
      }

      /** Same event shape as norse-buy-box, so sticky ATC stays in sync. */
      _dispatchChange(data, isSub) {
        const form = document.getElementById(`product-form-${this.sectionId}`);
        const variantId = form?.querySelector('input[name="id"]')?.value || null;
        document.dispatchEvent(new CustomEvent('norse-buy-box:change', {
          detail: {
            variantId,
            sellingPlanId: isSub ? data.sp : null,
            displayPrice: nstbbFormatMoney(isSub ? data.sub : data.ot),
            comparePrice: isSub ? nstbbFormatMoney(data.cmp) : null,
            discount: isSub ? this.querySelector('[data-nstbb-save-badge]')?.textContent : null,
          },
        }));
      }
    }
  );
}
